package com.htmlagent.site.theme

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.InputStyle
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.until
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px

/** The measure the whole page is written to. */
private val ContentWidth = 1180.px

/**
 * The height of the sticky bar.
 *
 * It is public because the document's `scroll-padding-top` has to be written against it: an
 * anchor that lands with its heading tucked under the bar is a link that looks broken.
 */
val NavHeight = 72.px

/** The top safe-area inset: zero in a browser tab, the camera's height in the full-screen app. */
const val SAFE_AREA_TOP = "env(safe-area-inset-top, 0px)"

/**
 * The smallest a thing you tap is allowed to be.
 *
 * It is a floor on the box and never on the type: the links stay at 14 px and grow their box
 * around it. Measured on a phone before this existed, the four audience calls were 25 px tall
 * and the footer anchors 14 px, which is a target you miss and then blame yourself for.
 */
val TapTarget = 44.px

/** The air around a band. It is the one measurement past the spacing scale, and it is why. */
private val SectionPad = 52.px
private val SectionPadCompact = 56.px

/** The browser mock-up in the hero, and the blurred light behind it. */
private val HeroArtWidth = 520.px
private val HeroGlowSize = 460.px

/** The rounded square an icon sits in, inside a card. */
private val IconTile = 44.px

/** The width one copy of the scrolling strip takes before it repeats. */

/** The email form in the closing call: wide enough for an address, no wider. */
private val FormWidth = 520.px

/** How far the light behind the mock-up is spread. */
private val GlowBlur = 90.px

// ---------------------------------------------------------------------------
// The page and its bands
// ---------------------------------------------------------------------------

/**
 * The landing root, and with it the whole public page.
 *
 * The application is deliberately quiet, because the right-hand panel shows pages of arbitrary
 * colors and the chrome must not compete with them. A landing has the opposite job: it is the
 * only thing on screen, and being quiet there reads as having nothing to say. So this file turns
 * three dials the application never touches — display sizes past the interface scale, weight 900,
 * and running copy set in the mono family — while every color, space and radius still comes from
 * the same tokens. It is not a second design system; it is the same one, louder.
 *
 * The rhythm is built from full-bleed bands alternating canvas and ink. The ink ones are not new
 * colors: [darkTokens] resolves the same tokens the dark way for that subtree, so a card inside a
 * band is written exactly like a card outside it.
 *
 * The style does not reuse `AppShellStyle`: that is a viewport-tall flexbox with
 * `overflow: hidden`, which is right for a workspace and would make a landing unscrollable.
 */
val LandingPageStyle = CssStyle.base {
    Modifier
        .backgroundColor(Tokens.bg.value())
        .color(Tokens.fg.value())
        .styleModifier {
            property("min-height", "100%")
            property("background-image", Tokens.backdrop.value())
        }
}

/**
 * Everything below the bar: the bands, and the horizontal clip they need.
 *
 * The clip used to sit on the page wrapper, and that is an ancestor of the sticky bar. A sticky
 * element inside a clip container stops being painted the moment it comes unstuck from its
 * position in the flow: the bar vanished on the first scroll while still reporting `top: 0`.
 * Measured here - with `overflow-x: visible` forced on the wrapper the bar paints and holds.
 *
 * Down here it does the same job, because what overflows is a full-bleed band and every band is
 * inside, while the bar is a sibling and no longer clipped by anything.
 */
val LandingContentStyle = CssStyle.base {
    // A full-bleed band must reach both edges; the shell's own padding would stop it.
    Modifier.styleModifier { property("overflow-x", "clip") }
}

/** The column everything is written in: full bleed outside, a readable measure inside. */
val LandingInnerStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("width", "100%")
            property("max-width", "$ContentWidth")
            property("margin", "0 auto")
            property("padding", "0 ${Tokens.spaceXl.value()}")
        }
    }
    Breakpoint.MD { Modifier.styleModifier { property("padding", "0 ${Tokens.spaceXxl.value()}") } }
}

/** The vertical air of a section. */
val LandingSectionStyle = CssStyle {
    base { Modifier.styleModifier { property("padding-block", "$SectionPadCompact") } }
    Breakpoint.MD { Modifier.styleModifier { property("padding-block", "$SectionPad") } }
}

/**
 * An ink band: the same tokens resolved dark, whatever mode the page is in.
 *
 * Alternating bands is what gives the page its beat, and doing it by re-resolving the tokens
 * rather than by adding a second palette means everything inside — cards, borders, the accent,
 * the focus ring — lands on values already calibrated against a dark surface.
 *
 * [darkTokens] alone is not enough, and finding that out is what the first draft was for: in
 * light mode it inverts and the band is unmissable, but in dark mode it resolves to exactly what
 * the page already was and the band disappears. So the band also moves the three surface tokens
 * up one rung of the same scale. Against white it still reads as ink; against the dark canvas it
 * reads as a slab lifted off it. One rule, both modes, and the cards inside go on asking for
 * [Tokens.surface] without knowing any of this happened.
 */
val LandingBandStyle = CssStyle.base {
    Modifier
        .darkTokens()
        .setVariable(Tokens.bg, Slate.Dark.S3)
        .setVariable(Tokens.surface, Slate.Dark.S5)
        .setVariable(Tokens.raised, Slate.Dark.S6)
        .backgroundColor(Tokens.bg.value())
        .color(Tokens.fg.value())
}

// ---------------------------------------------------------------------------
// Type
// ---------------------------------------------------------------------------

/**
 * The small mono label that names a section before its headline.
 *
 * Uppercase with generous tracking, which is the one place letters need pushing apart rather
 * than pulling together: capitals at this size collide into a single shape.
 */
val EyebrowStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("display", "block")
            property("font-family", Tokens.fontMono.value())
            property("font-size", "${Tokens.textXs.value()}")
            property("letter-spacing", Tracking.Eyebrow)
            property("text-transform", "uppercase")
        }
    }
    // On ink the label carries the accent instead of grey.
    cssRule("[data-tone=\"accent\"]") { Modifier.color(Tokens.accent.value()) }
}

/** A section headline: the loudest thing in its band, and never more than four words. */
val DisplayTitleStyle = CssStyle {
    base {
        Modifier.color(Tokens.fg.value()).styleModifier {
            property("margin", "${Tokens.spaceMd.value()} 0 0")
            property("font-family", Tokens.fontSans.value())
            property("font-size", "${Tokens.textDisplay.value()}")
            property("font-weight", "${Weight.Black}")
            property("line-height", Leading.Display)
            property("letter-spacing", Tracking.Display)
            property("text-transform", "uppercase")
        }
    }
    // The hero and the closing call, which are a step above a section headline.
    cssRule("[data-size=\"hero\"]") {
        Modifier.styleModifier { property("font-size", "${Tokens.textHero.value()}") }
    }
}

/** The half of a headline that carries the color. Applied to a `span` inside the heading. */
val AccentLineStyle = CssStyle.base {
    Modifier.color(Tokens.accent.value()).styleModifier { property("display", "block") }
}

/**
 * The short accent rule under a centred headline. It is three tokens wide and two pixels
 * tall: an ornament, so it is `aria-hidden` where it is painted.
 */
val TitleRuleStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.accent.value()).styleModifier {
        property("width", "${Tokens.spaceXxxl.value()}")
        property("height", "2px")
        property("margin", "${Tokens.spaceLg.value()} auto 0")
        property("border-radius", "${Tokens.radiusFull.value()}")
    }
}

/**
 * Running copy on the landing, set in the mono family.
 *
 * It is the single strongest signal the page has, and it is borrowed on purpose: prose in a
 * monospace face reads as something written rather than something marketed, which is what a
 * tool that writes code should sound like.
 */
val LandingLeadStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("margin", "${Tokens.spaceLg.value()} 0 0")
            property("font-family", Tokens.fontMono.value())
            property("font-size", "${Tokens.textBase.value()}")
            property("line-height", Leading.Normal)
            property("max-width", "56ch")
        }
    }
    // The same copy centred under a section headline.
    cssRule("[data-align=\"center\"]") {
        Modifier.styleModifier { property("margin", "${Tokens.spaceLg.value()} auto 0") }
    }
}

/**
 * A centred section header: eyebrow, headline, rule.
 *
 * Its air is measured against the screen and not chosen: at 48px the steps and the figures did
 * not fit in a laptop's window, so the section arrived by its anchor already cut.
 */
val SectionHeadStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("text-align", "center")
            property("margin-bottom", "${Tokens.spaceXl.value()}")
        }
    }
    Breakpoint.MD {
        Modifier.styleModifier { property("margin-bottom", "${Tokens.spaceXxl.value()}") }
    }
}

/**
 * The page itself arriving, and on its way out.
 *
 * It lives here and not in `Styles.kt` because both routes use it and this is the file the two
 * of them already share. The exit is a suffix rule for the same reason every other state on this
 * page is: two classes setting `opacity` tie on specificity and the winner would be whichever
 * Kobweb happened to register last.
 */
val FadingPageStyle = CssStyle {
    base {
        Modifier.styleModifier {
            // `backwards` and not `both`, which is what the first attempt used and is why the
            // exit never showed: `both` keeps the last keyframe applying once the animation is
            // over, an animated value beats a transitioned one, and `opacity: 1` therefore won
            // against the fade out for good. `backwards` gives the same protection against a
            // flash of the unstyled first frame and lets go at the end.
            property("animation", "ha-page-in ${Motion.PageEnter} ${Motion.Curve} backwards")
            property("transition", "opacity ${Motion.PageLeave} ${Motion.Ease}")
        }
    }
    cssRule("[data-leaving=\"true\"]") { Modifier.styleModifier { property("opacity", "0") } }
}

// ---------------------------------------------------------------------------
// Reveal
// ---------------------------------------------------------------------------

/**
 * A block that rises into place the first time it is scrolled into view.
 *
 * The shown state is a suffix rule and not a second style so it wins on specificity without
 * anyone having to reason about the order Kobweb registers styles in. Under reduced motion the
 * global rule collapses the transition to nothing, so the block simply appears.
 */
val RevealStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("opacity", "0")
            property("transform", "translateY(28px)")
            property(
                "transition",
                "opacity ${Motion.Reveal} ${Motion.Curve}, transform ${Motion.Reveal} ${Motion.Curve}",
            )
        }
    }
    cssRule("[data-shown=\"true\"]") {
        Modifier.styleModifier {
            property("opacity", "1")
            property("transform", "none")
        }
    }
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

/**
 * The sticky bar. Transparent over the hero and glass once the page has moved under it.
 *
 * The glass is a layer of its own that fades in, not properties of the bar: the blur was not
 * in the bar's transition, so it switched on in a single frame while the fill was still fading,
 * and animating a blur's radius re-runs it on every frame.
 */
val LandingNavStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("position", "sticky")
            property("top", "0")
            property("z-index", "${Layer.Popover}")
            // Keeps the glass layer's `z-index: -1` inside the bar instead of under the page.
            property("isolation", "isolate")
            // In the installed app the page runs full screen and reaches up beside the front
            // camera; the bar grows by that inset so the logo and the menu stay below it.
            property("padding-top", SAFE_AREA_TOP)
            property("height", "calc($NavHeight + $SAFE_AREA_TOP)")
            property("display", "flex")
            property("align-items", "center")
        }
    }
    cssRule("::before") {
        Modifier.styleModifier {
            property("content", "\"\"")
            property("position", "absolute")
            property("inset", "0")
            property("z-index", "-1")
            property("background", Tokens.glassPanelFill.value())
            property("backdrop-filter", Glass.Blur)
            property("-webkit-backdrop-filter", Glass.Blur)
            property("border-bottom", "1px solid ${Tokens.line.value()}")
            property("opacity", "0")
            property("pointer-events", "none")
            property("transition", "opacity ${Motion.Base} ${Motion.Ease}")
        }
    }
    // What the bar becomes once it has something behind it to separate itself from.
    cssRule("[data-scrolled=\"true\"]::before") {
        Modifier.styleModifier { property("opacity", "1") }
    }
}

/** The bar's own row: mark on the left, links in the middle, actions on the right. */
val LandingNavRowStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceLg.value()}")
    }
}

/** The wordmark: the product glyph and its name. */
val LandingMarkStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("min-height", "$TapTarget")
        property("gap", "${Tokens.spaceSm.value()}")
        // Read through `styleModifier` and not `Modifier.fontFamily`, which rejects a stack with
        // commas at runtime and takes `initSilk` down with it.
        property("font-family", Tokens.fontDisplay.value())
        property("font-weight", "${Weight.Bold}")
        property("letter-spacing", Tracking.Tight)
        property("text-decoration", "none")
    }
}

/** How much bigger the wordmark's tile is than a small control. */
/**
 * The two lines of the wordmark, stacked beside the tile.
 *
 * `align-items: flex-start` and not the default stretch: the tagline is the shorter of the two
 * and stretching it would leave the tracking of its last letter hanging past the name.
 */
val LandingMarkTextStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "inline-flex")
        property("flex-direction", "column")
        property("align-items", "flex-start")
        property("justify-content", "center")
        property("line-height", Leading.Tight)
    }
}

/**
 * The line under the name: what the product does, in the voice of a label rather than a title.
 *
 * It carries its own tracking because capitals this small collide, and its own weight because
 * the wordmark's is set for a name three letters long and would turn a whole phrase into a
 * second headline. The stretch of [LandingMarkNameStyle] does not reach it on purpose: a line
 * of small capitals is where a scale starts to show.
 */
val LandingMarkTaglineStyle = CssStyle {
    // One line or none. Measured at 390 px with the menu button in the bar, the mark plus the
    // pill plus the button came to 397: seven pixels of horizontal scroll on the whole page.
    // The tagline is the part that can go, because the name alone is still the mark.
    base { Modifier.styleModifier { property("display", "none") } }
    Breakpoint.SM {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("display", "block")
            property("font-size", "$MarkTaglineSize")
            property("font-weight", "${Weight.Semibold}")
            property("letter-spacing", MARK_TAGLINE_TRACKING)
            property("text-transform", "uppercase")
            property("white-space", "nowrap")
        }
    }
    // It goes at LG, which is where the five anchors come back and the row is tightest: measured
    // there in English, mark plus anchors plus actions come to 836 of the 960 that width has,
    // and these 139 pixels are what turns that into an overflow.
    Breakpoint.LG { Modifier.styleModifier { property("display", "none") } }
    // From XL the measure is fixed at 1180 and the same row needs 975 of it, so the line comes
    // back rather than being lost to a width that has room for it.
    Breakpoint.XL { Modifier.styleModifier { property("display", "block") } }
}

/**
 * The tagline's size, and it is a measurement of its own rather than the smallest text token.
 *
 * The token is 12.3px, which next to a 16px name gave the two lines almost the same presence
 * and left neither of them looking like the mark. Comfortaa stops at 700, so the name cannot be
 * made heavier than it already is: the weight has to be read against something, and what it
 * reads against is this.
 */
private val MarkTaglineSize = 9.px

/**
 * The air between the tagline's capitals, and it sits here rather than in the tracking scale
 * because it is between two of its steps and belongs to this one line.
 *
 * [Tracking.Eyebrow] is calibrated for capitals at 12px and at 8px it opens the phrase into
 * separate letters; [Tracking.Loose] is the correction for a two-letter code and closes a whole
 * phrase too far. This is the middle.
 */
private const val MARK_TAGLINE_TRACKING = "0.08em"

/**
 * The name in the wordmark, stretched.
 *
 * Comfortaa has no width axis -the variable font Google serves carries weight and nothing else-
 * so the only way to widen it is to scale it, and a scale needs a box: `transform` does nothing
 * to an inline run of text. The origin is the left edge so the name grows away from the tile
 * instead of climbing over it, and the four pixels it gains are not in the layout box, which is
 * why nothing after it moves.
 */
val LandingMarkNameStyle = CssStyle.base {
    Modifier.fontSize(Tokens.textMd.value()).styleModifier {
        property("display", "inline-block")
        property("font-weight", "${Weight.Bold}")
        property("transform", "scaleX($MARK_NAME_STRETCH)")
        property("transform-origin", "left center")
    }
}

/** How much wider than drawn the name is set. */
private const val MARK_NAME_STRETCH = 1.1

/**
 * The side of the wordmark's tile.
 *
 * It is a measurement of its own and not one of the control tokens: the mark is a drawing with
 * a face, glasses and a mouth inside it, and at the size a small control wants those stop
 * reading. Nothing else in the bar is this size, so there is no token to follow.
 */
private val MarkTileSide = 42.px

/**
 * The tile the mark sits on inside the wordmark.
 *
 * The mark's face is transparent, so the tile is not only a frame: it is what shows through the
 * figure and gives it its second color. That is why it reads [Tokens.markTile] and not one of
 * the surface tokens around it.
 */
val LandingMarkTileStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.markTile.value()).styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("width", "$MarkTileSide")
        property("height", "$MarkTileSide")
        property("border-radius", "${Tokens.radiusMd.value()}")
    }
}

/** The middle group of anchors. It is the first thing to go on a narrow screen. */
val LandingNavLinksStyle = CssStyle {
    base { Modifier.styleModifier { property("display", "none") } }
    // LG and not MD. Measured at 768: mark 189 + five anchors 362 + actions 216 + 64 of
    // padding is 831 in a 768 viewport, so the bar overflowed by 63 px and the page scrolled
    // sideways. Five anchors need a desktop; below one, the sheet carries them.
    Breakpoint.LG {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            // 16 and not the 24 this used to be. Four gaps at the wide value are 96 px, and
            // measured with the second door in the bar the row came to 1183 in a measure of
            // 1180 - in English, which is where the anchors are longest. It is the cheapest
            // 32 px on the row and nobody reads an anchor as further away for it.
            property("gap", "${Tokens.spaceLg.value()}")
            // Beside the mark, not centred. `margin: 0 auto` centres a thing in what is left
            // over, and what is left over here is not the page: with a mark of 189 against
            // actions of 414 the anchors sat 182 px off the middle of the column everything
            // else is written to, which reads as a header that does not line up with its page.
            // Two groups, one at each end, is the layout this bar actually is.
            property("margin-left", "${Tokens.spaceXl.value()}")
        }
    }
}

/**
 * One anchor, in the bar or in the sheet.
 *
 * The minimum height is the pointer target and not the type: the text is 14 px tall and on a
 * phone every one of these was a 14 px strip to hit.
 */
val LandingNavLinkStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("min-height", "$TapTarget")
            property("font-size", "${Tokens.textSm.value()}")
            property("font-weight", "${Weight.Semibold}")
            property("text-decoration", "none")
            // A menu item never wraps. Without this the anchors were the one thing in the row
            // that gave way when the bar ran short, and "Qué te llevas" arrived on three lines
            // at 46 px wide - a label nobody reads and a bar nobody trusts.
            property("white-space", "nowrap")
            property("transition", "color ${Motion.Fast} ${Motion.Ease}")
        }
    }
    hover { Modifier.color(Tokens.fg.value()) }
    focusVisible { Modifier.focusOutline() }
}

/**
 * The skip link: off screen until it has focus, and then a pill in the top corner.
 *
 * It is moved with `top` and not with `display: none`, which would take it out of the tab order
 * and leave the page with a link nothing can reach.
 */
val SkipLinkStyle = CssStyle {
    base {
        Modifier
            .color(Tokens.onAccent.value())
            .backgroundColor(Tokens.accent.value())
            .styleModifier {
                property("position", "fixed")
                property("top", "-100px")
                property("left", "${Tokens.spaceLg.value()}")
                property("z-index", "${Layer.Skip}")
                property("display", "inline-flex")
                property("align-items", "center")
                property("min-height", "$TapTarget")
                property("padding", "0 ${Tokens.spaceXl.value()}")
                property("border-radius", "${Tokens.radiusFull.value()}")
                property("font-size", "${Tokens.textSm.value()}")
                property("font-weight", "${Weight.Semibold}")
                property("text-decoration", "none")
                property("transition", "top ${Motion.Fast} ${Motion.Ease}")
            }
    }
    focusVisible {
        Modifier.styleModifier { property("top", "${Tokens.spaceLg.value()}") }
            .focusOutline()
    }
}

/** The one control that opens the sections on a screen too narrow to show them. */
val NavMenuButtonStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("width", "$TapTarget")
            property("height", "$TapTarget")
            property("padding", "0")
            property("background", "transparent")
            property("border", "none")
            property("cursor", "pointer")
        }
    }
    Breakpoint.LG { Modifier.styleModifier { property("display", "none") } }
    hover { Modifier.color(Tokens.fg.value()) }
    focusVisible { Modifier.focusOutline() }
}

/**
 * The sheet the sections drop into below a desktop.
 *
 * It is laid out at all widths and only revealed by the data attribute, so the anchors exist in
 * the document either way; what keeps the closed sheet out of the tab order is `inert` on the
 * element, which `aria-hidden` does not do.
 */
val NavSheetStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "none")
            property("flex-direction", "column")
            property("gap", "${Tokens.spaceMd.value()}")
            property("position", "absolute")
            property("top", "100%")
            property("left", "0")
            property("right", "0")
            property("padding", "${Tokens.spaceLg.value()} ${Tokens.spaceXl.value()}")
            // Opaque, unlike the bar above it. The bar is glass because it sits on a headline
            // it must not hide; a menu is read, and the hero's glow coming through it was the
            // difference between five links and five links with a bruise behind them.
            property("background", Tokens.raised.value())
            property("border-bottom", "1px solid ${Tokens.line.value()}")
            property("box-shadow", Tokens.glassPanelShadow.value())
        }
    }
    cssRule("[data-open=\"true\"]") { Modifier.styleModifier { property("display", "flex") } }
    // Above the breakpoint the bar carries them itself; the sheet never opens there.
    Breakpoint.LG { Modifier.styleModifier { property("display", "none") } }
}

/** The anchors inside the sheet: one per row, each a full-width target. */
val NavSheetLinksStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
    }
}

/** The two switches at the foot of the sheet, which the bar hides at this width. */
val NavSheetSwitchesStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceXs.value()}")
        property("padding-top", "${Tokens.spaceSm.value()}")
        property("border-top", "1px solid ${Tokens.line.value()}")
    }
}

/**
 * The right-hand group: the two switches, the way back in and the call to action.
 *
 * It also carries the size the pills in it take, as two variables the pills read inline. That
 * detour exists because both properties are set by the pill variant as well, two classes
 * setting the same property tie, and the winner would be whichever Kobweb registered last. An
 * inline value read from a variable wins outright, and the width is still decided by a media
 * query — just one that lives on this element instead of on the button.
 */
val LandingNavActionsStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceSm.value()}")
            property("margin-left", "auto")
            // The same values the pill variant carries, so nothing moves at these widths.
            property("--nav-pill-pad", "$PillPadCompact")
            property("--nav-pill-height", "${Tokens.controlLg.value()}")
        }
    }
    Breakpoint.SM {
        Modifier.styleModifier { property("--nav-pill-pad", "${Tokens.spaceXl.value()}") }
    }
    /*
     * Where the bar fills up, its pills come down a step.
     *
     * The hero is where the decision is made and its pills are sized for it; the bar carries a
     * shortcut to the same decision and does not need to shout as loud. It came off a
     * measurement: with the second door in the row the bar came to 1216 in a measure of 1180,
     * so the actions ran 35 px past the right edge of the column the whole page is written to,
     * which is what a header looks like when it does not line up with its own page.
     *
     * Only padding and height move; the type stays, and 40 px is still a comfortable target on
     * the pointer a desktop implies.
     */
    Breakpoint.LG {
        Modifier.styleModifier {
            property("--nav-pill-pad", "${Tokens.spaceLg.value()}")
            property("--nav-pill-height", "${Tokens.controlMd.value()}")
        }
    }
}

/** Reads the bar's size onto a pill, inline, where no other class can tie with it. */
fun Modifier.navPill(): Modifier = styleModifier {
    property("min-height", "var(--nav-pill-height)")
    property("padding", "0 var(--nav-pill-pad)")
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

/**
 * What a pill gives up below a tablet so a pair of them stays a pair.
 *
 * Measured on the hero at 390 px: 342 px of usable width against pills of 190 and 191 with a
 * 12 px gap — 393, so they wrapped and the page offered two stacked buttons where the design
 * has one decision with two answers. The labels are only 142 and 141 of that; the rest is
 * padding, which is why padding is what moves.
 *
 * The type comes down with it because padding alone is not enough: with 24 px of padding the
 * pair is still too wide at 390. Measured again after the squeeze, 12 px landed the pair on
 * exactly the 312 px a 360 px phone has, which wraps; 10 px leaves 16 px of room there. Below
 * about 340 px they stack, and stacking is the right answer once the labels genuinely do not
 * fit. Neither value touches `controlLg`, so the 44 px target survives the squeeze.
 */
private val PillPadCompact = 10.px
private val PillTextCompact = 0.875.cssRem

/** The solid pill: one per screen, and it always goes to the same place. */
val PillPrimaryVariant = ButtonStyle.addVariant {
    base {
        Modifier.color(Tokens.onAccent.value()).backgroundColor(Tokens.accent.value()).styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceSm.value()}")
            property("min-height", "${Tokens.controlLg.value()}")
            // See [PillPad]: on a phone a pill gives up padding rather than its place in the row.
            property("padding", "0 $PillPadCompact")
            property("font-size", "$PillTextCompact")
            property("border", "none")
            property("border-radius", "${Tokens.radiusFull.value()}")
            property("font-weight", "${Weight.Bold}")
            property("white-space", "nowrap")
            property("transition", "transform ${Motion.Base} ${Motion.Curve}, background ${Motion.Base} ${Motion.Ease}")
        }
    }
    // Color only. The reference lifts nothing on hover, and a solid button that rises is the
    // kind of flourish this page had one of too many.
    Breakpoint.SM {
        Modifier.styleModifier {
            property("padding", "0 ${Tokens.spaceXl.value()}")
            property("font-size", "1rem")
        }
    }
    hover { Modifier.backgroundColor(Tokens.accentHover.value()) }
    focusVisible { Modifier.focusOutline() }

    // Silk puts the children in a row of its own, so the gap between the label and its glyph
    // belongs on that child and not on the button.
    cssRule(" > *") { Modifier.styleModifier { property("gap", "${Tokens.spaceSm.value()}") } }
}

/** The outline pill beside it. Same shape, so the pair reads as one decision with two answers. */
val PillGhostVariant = ButtonStyle.addVariant {
    base {
        Modifier.color(Tokens.fg.value()).styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceSm.value()}")
            property("min-height", "${Tokens.controlLg.value()}")
            // See [PillPadCompact]: the pair shrinks together or it stops being a pair.
            property("padding", "0 $PillPadCompact")
            property("font-size", "$PillTextCompact")
            property("background", "transparent")
            property("border", "1px solid ${Tokens.lineStrong.value()}")
            property("border-radius", "${Tokens.radiusFull.value()}")
            property("font-weight", "${Weight.Semibold}")
            property("white-space", "nowrap")
            property(
                "transition",
                "background ${Motion.Base} ${Motion.Ease}, color ${Motion.Base} ${Motion.Ease}",
            )
        }
    }
    // The outline button fills in rather than tinting: it is the reference's one place where a
    // secondary action commits to a color, and it reads as an answer rather than a hint.
    Breakpoint.SM {
        Modifier.styleModifier {
            property("padding", "0 ${Tokens.spaceXl.value()}")
            property("font-size", "1rem")
        }
    }
    hover {
        Modifier.color(Tokens.onAccent.value()).styleModifier {
            property("background", Tokens.accent.value())
            property("border-color", Tokens.accent.value())
        }
    }
    focusVisible { Modifier.focusOutline() }
}

/** The focus ring, written once so no control invents its own. */
fun Modifier.focusOutline(): Modifier = styleModifier {
    property("outline", "2px solid ${Tokens.accent.value()}")
    property("outline-offset", "2px")
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

/** Copy on the left, the mock-up on the right; stacked below a desktop. */
val HeroStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "grid")
            property("gap", "${Tokens.spaceXxxl.value()}")
            property("align-items", "center")
            property("padding-block", "$SectionPadCompact")
        }
    }
    // LG and not MD, for the same reason the bar waits: at 768 the two columns left the copy
    // 354 px wide, and the two hero pills need 393 to sit side by side. One column at tablet
    // widths gives the headline its measure back and the pair its row.
    Breakpoint.LG {
        Modifier.styleModifier {
            // The copy gets the wider half: four short facts on one line is the difference
            // between a row of claims and a paragraph of them.
            property("grid-template-columns", "1.08fr 0.92fr")
            property("padding-block", "$SectionPad")
            // Enough to be the screen without pretending the page ends here: the top of the
            // next band has to show, or nobody scrolls.
            property("min-height", "calc(84vh - $NavHeight)")
        }
    }
}

/** The dark chip above the headline, with its live dot. */
val HeroBadgeStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceSm.value()}")
        property("padding", "${Tokens.spaceXs.value()} ${Tokens.spaceMd.value()}")
        property("background", Tokens.accentSurface.value())
        property("border", "1px solid ${Tokens.accentLine.value()}")
        property("border-radius", "${Tokens.radiusFull.value()}")
    }
}

/** The dot in the chip, which is the only piece of the hero that keeps moving. */
val HeroBadgeDotStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.accent.value()).styleModifier {
        property("width", "${Tokens.dot.value()}")
        property("height", "${Tokens.dot.value()}")
        property("border-radius", "${Tokens.radiusFull.value()}")
        property("animation", "ha-pulse ${Motion.PulseCycle} ${Motion.Breathe} infinite alternate")
    }
}

/** The two buttons under the headline. */
val HeroActionsStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            // Tighter than the desktop gap: the two pills are a pair and on a phone the
            // difference between fitting and stacking is measured in single pixels.
            property("gap", "${Tokens.spaceSm.value()}")
            property("margin-top", "${Tokens.spaceXl.value()}")
        }
    }

    Breakpoint.SM {
        Modifier.styleModifier { property("gap", "${Tokens.spaceMd.value()}") }
    }
}

/** The row of four short facts, each with an accent dot. */
val HeroFactsStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-wrap", "wrap")
        // Tight enough that the four fit on one line at the narrowest desktop, in both
        // languages: split over two lines they stop reading as a row of claims.
        property("gap", "${Tokens.spaceSm.value()} ${Tokens.spaceLg.value()}")
        property("margin-top", "${Tokens.spaceXxl.value()}")
        property("padding", "0")
        property("list-style", "none")
    }
}

/** One fact. The dot is drawn with a pseudo-element so the text stays a single node. */
val HeroFactStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceSm.value()}")
        property("font-family", Tokens.fontMono.value())
        property("font-size", "${Tokens.textXs.value()}")
    }
}

/** The dot beside a fact. */
val HeroFactDotStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.accent.value()).styleModifier {
        property("width", "${Tokens.spaceXs.value()}")
        property("height", "${Tokens.spaceXs.value()}")
        property("border-radius", "${Tokens.radiusFull.value()}")
        property("flex-shrink", "0")
    }
}

/** The stage the mock-up floats on. It holds the glow, so the glow can sit behind. */
val HeroArtStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("position", "relative")
            property("display", "flex")
            property("justify-content", "center")
            property("align-items", "center")
        }
    }
    Breakpoint.MD { Modifier.styleModifier { property("justify-content", "flex-end") } }
}

/**
 * The light behind the mock-up: a blurred accent disc that breathes.
 *
 * It is a painted element and not a `box-shadow` because a keyframe written as raw CSS cannot
 * read a token; this way the color comes from the system like everything else.
 *
 * Not below [Breakpoint.MD]. On a phone the mock-up lands at the bottom edge of the first screen,
 * and Chrome on Android paints the gesture bar under it with one flat color: the glow ended in
 * a blue band cut against it. Measured on the device, the edge went from `rgb(140,183,232)` to
 * `rgb(227,236,247)` without it; what remains is the page's own backdrop.
 */
val HeroGlowStyle = CssStyle {
    base {
        Modifier.backgroundColor(Tokens.accent.value()).styleModifier {
            property("position", "absolute")
            property("width", "$HeroGlowSize")
            property("height", "$HeroGlowSize")
            property("border-radius", "${Tokens.radiusFull.value()}")
            property("filter", "blur($GlowBlur)")
            property("opacity", "0.55")
            property("animation", "ha-glow ${Motion.GlowCycle} ${Motion.Breathe} infinite")
            property("pointer-events", "none")
        }
    }
    until(Breakpoint.MD) { Modifier.styleModifier { property("display", "none") } }
}

/** The browser mock-up itself, which rises and settles forever. */
val HeroFrameStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "relative")
        property("width", "100%")
        property("max-width", "$HeroArtWidth")
        property("border-radius", "${Tokens.radiusLg.value()}")
        property("overflow", "hidden")
        property("background", Tokens.surface.value())
        property("border", Tokens.glassEdge.value())
        property("box-shadow", Tokens.glassPanelShadow.value())
        property("animation", "ha-float ${Motion.FloatCycle} ${Motion.Breathe} infinite")
    }
}

/** The mock-up's title bar: traffic lights and an address. */
val HeroFrameBarStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceSm.value()}")
        property("padding", "${Tokens.spaceSm.value()} ${Tokens.spaceMd.value()}")
        property("border-bottom", Tokens.glassDivider.value())
        property("background", Tokens.scrim.value())
    }
}

/** The address in that bar. */
val HeroFrameAddressStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("flex", "1")
        property("padding", "${Tokens.spaceXxs.value()} ${Tokens.spaceSm.value()}")
        property("background", Tokens.well.value())
        property("border-radius", "${Tokens.radiusSm.value()}")
        property("font-family", Tokens.fontMono.value())
        property("font-size", "${Tokens.textXs.value()}")
        property("text-align", "center")
    }
}

/**
 * What is drawn inside the mock-up: a page, in the shape a page has.
 *
 * Nothing here is an image. The point of the product is that it writes pages out of nothing but
 * CSS, and a stock photograph in the hero would be arguing against it.
 */
val MockPageStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.bg.value()).styleModifier {
        property("padding", "${Tokens.spaceLg.value()}")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Tokens.spaceMd.value()}")
    }
}

/** The mock page's own hero: an accent block with two lines knocked out of it. */
val MockBannerStyle = CssStyle.base {
    Modifier.styleModifier {
        property("height", "${Tokens.barCard.value()}")
        property("border-radius", "${Tokens.radiusMd.value()}")
        property("padding", "${Tokens.spaceMd.value()}")
        property("display", "flex")
        property("flex-direction", "column")
        property("justify-content", "flex-end")
        property("gap", "${Tokens.spaceXs.value()}")
        property(
            "background",
            "linear-gradient(135deg, ${Tokens.accent.value()}, ${Tokens.accentSurface.value()})",
        )
    }
}

/** A line of fake text. Its width is set where it is used, because that is what varies. */
val MockLineStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("height", "${Tokens.spaceSm.value()}")
            property("border-radius", "${Tokens.radiusFull.value()}")
            property("background", Tokens.skeletonDone.value())
        }
    }
    // Knocked out of the accent block, the line takes [Tokens.onAccent] softened with opacity
    // rather than naming a fourth white: what reads on the solid reads on the gradient made
    // from it.
    cssRule("[data-on-accent=\"true\"]") {
        Modifier.backgroundColor(Tokens.onAccent.value()).styleModifier { property("opacity", "0.75") }
    }
}

/** The three cards under the mock hero. */
val MockCardsStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, 1fr)")
        property("gap", "${Tokens.spaceSm.value()}")
    }
}

/** One of them. */
val MockCardStyle = CssStyle.base {
    Modifier.styleModifier {
        property("height", "${Tokens.barButton.value()}")
        property("border-radius", "${Tokens.radiusSm.value()}")
        property("background", Tokens.surface.value())
        property("border", Tokens.glassDivider.value())
    }
}

// ---------------------------------------------------------------------------
// How it works
// ---------------------------------------------------------------------------

/**
 * How long the panel takes to arrive from the side the pressed tab is on.
 *
 * It is an animation and not a transition because the panel's own element stays put: what
 * changes is the writing inside it, and an animation restarts when its name does.
 */
private const val STEP_PANEL_MS = 320

/** Which way the panel's writing comes in from: the component writes it per press. */
const val STEP_PANEL_FROM = "--step-from"

/** The three tabs, one row of equal thirds. On a phone this layout is not used at all. */
val StepTabsStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        property("gap", "${Tokens.spaceMd.value()}")
        property("margin-top", "${Tokens.spaceXl.value()}")
    }
}

/**
 * One tab: its numeral, its kicker and the promise in four words.
 *
 * The chosen one is a suffix rule on the same class and not a second class: two classes setting
 * the same property tie, and the winner is whichever Kobweb registered last.
 */
val StepTabStyle = CssStyle {
    base {
        Modifier
            .color(Tokens.fg.value())
            .styleModifier { property("border-radius", "${Tokens.radiusMd.value()}") }
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "${Tokens.spaceMd.value()}")
                property("width", "100%")
                property("min-width", "0")
                property("min-height", "64px")
                property("padding", "0 ${Tokens.spaceLg.value()}")
                property("text-align", "left")
                property("cursor", "pointer")
                property("background", Tokens.well.value())
                property("border", Tokens.glassEdge.value())
                property(
                    "transition",
                    "background ${Motion.Base} ${Motion.Ease}, border-color ${Motion.Base} ${Motion.Ease}",
                )
            }
    }
    hover { Modifier.styleModifier { property("border-color", Tokens.lineStrong.value().toString()) } }
    focusVisible { Modifier.focusOutline() }
    cssRule("[data-on=\"true\"]") {
        Modifier.backgroundColor(Tokens.accentSurface.value()).styleModifier {
            property("border-color", Tokens.accent.value().toString())
        }
    }
    // Silk wraps a button's children in a row of its own and centres it, so the gap and the
    // alignment have to go there: centred, the mark and its text sat away from the left edge.
    cssRule(" > *") {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "flex-start")
            property("gap", "${Tokens.spaceMd.value()}")
            property("width", "100%")
            property("min-width", "0")
        }
    }
}

/**
 * The mark of a step, on its tab or its row: the drawing of what happens in it.
 *
 * A drawing and not the number, which the kicker beside it already says in words. A quiet tile
 * while the step waits, the accent once it is the one being read.
 */
val StepMarkStyle = CssStyle {
    base {
        Modifier
            .color(Tokens.lineStrong.value())
            .styleModifier { property("border-radius", "${Tokens.radiusSm.value()}") }
            .styleModifier {
                property("flex", "0 0 auto")
                property("width", "${Tokens.controlMd.value()}")
                property("height", "${Tokens.controlMd.value()}")
                property("display", "grid")
                property("place-items", "center")
                property("background", Tokens.raised.value())
                property(
                    "transition",
                    "background ${Motion.Base} ${Motion.Ease}, color ${Motion.Base} ${Motion.Ease}",
                )
            }
    }
    cssRule("[data-on=\"true\"]") {
        Modifier.color(Tokens.onAccent.value()).backgroundColor(Tokens.accent.value())
    }
}

/** What a tab says under its kicker: the promise alone, never the whole sentence. */
val StepTabTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("display", "block")
        property("font-weight", "${Weight.Bold}")
        property("line-height", Leading.Snug)
    }
}

/** The track under the tabs. */
val StepRailStyle = CssStyle.base {
    Modifier.styleModifier {
        property("border-radius", "${Tokens.radiusFull.value()}")
        property("height", "3px")
        property("margin-top", "${Tokens.spaceMd.value()}")
        property("background", Tokens.line.value())
        property("overflow", "hidden")
    }
}

/**
 * The part of the track under the chosen tab.
 *
 * It travels with a transform and not with a `left`: one is composited and the other lays the
 * page out again on every frame.
 */
val StepRailFillStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.accent.value()).styleModifier {
        property("border-radius", "${Tokens.radiusFull.value()}")
        property("width", "calc(100% / 3)")
        property("height", "100%")
        property("transition", "transform ${STEP_PANEL_MS}ms ${Motion.Curve}")
    }
}

/** The one panel the three tabs share. */
val StepPanelStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.surface.value())
            .styleModifier { property("border-radius", "${Tokens.radiusLg.value()}") }
            .styleModifier {
                property("position", "relative")
                property("margin-top", "${Tokens.spaceLg.value()}")
                property("min-height", "17rem")
                property("border", Tokens.glassEdge.value())
                property("overflow", "hidden")
            }
    }
}

/**
 * What is written on the panel, and the only thing that moves when the step changes.
 *
 * The animation arrives with its name alternating between two identical sets of keyframes: the
 * element stays, so a name that repeats would not play a second time.
 */
val StepPanelInkStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "flex-start")
        property("gap", "${Tokens.spaceSm.value()}")
        property("padding", "${Tokens.spaceXl.value()}")
        // Wide enough that the longest step's body reads in four lines: at 44rem it took five
        // and the section no longer fitted in a laptop's window.
        property("max-width", "52rem")
        // Everything but the name, which is what the component alternates.
        property("animation-duration", "${STEP_PANEL_MS}ms")
        property("animation-timing-function", Motion.Curve)
        property("animation-fill-mode", "backwards")
    }
}

/** A step's whole sentence, on the panel. Bigger than a tab's promise: it is the thing being read. */
val StepPanelTitleStyle = CssStyle {
    base {
        Modifier.color(Tokens.fg.value()).styleModifier {
            property("margin", "0")
            property("font-family", Tokens.fontSans.value())
            property("font-size", "${Tokens.textLg.value()}")
            property("font-weight", "${Weight.Black}")
            property("line-height", Leading.Tight)
            property("letter-spacing", Tracking.Display)
        }
    }
    Breakpoint.MD {
        Modifier.styleModifier { property("font-size", "${Tokens.textXl.value()}") }
    }
}

/**
 * The accordion, which is what a phone gets instead of tabs.
 *
 * It claims the height of the screen so the step being read is not a strip: the open row takes
 * whatever the two closed ones leave.
 */
val StepAccordionStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Tokens.spaceMd.value()}")
        property("margin-top", "${Tokens.spaceXl.value()}")
        // The screen minus its header: the open step fills what is left instead of sitting in
        // a strip, and the section still arrives whole when its anchor is pressed.
        property("min-height", "min(64vh, 30rem)")
    }
}

/** One row of the accordion. Open, it grows into the room the others are not using. */
val StepRowStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.well.value())
            .styleModifier { property("border-radius", "${Tokens.radiusLg.value()}") }
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("flex", "0 0 auto")
                property("overflow", "hidden")
                property("border", Tokens.glassEdge.value())
                property(
                    "transition",
                    "background ${Motion.Base} ${Motion.Ease}, border-color ${Motion.Base} ${Motion.Ease}",
                )
            }
    }
    cssRule("[data-open=\"true\"]") {
        Modifier.backgroundColor(Tokens.surface.value()).styleModifier {
            property("flex", "1 1 auto")
            property("border-color", Tokens.accent.value().toString())
        }
    }
}

/** The row's header, which is the only thing that can be pressed. */
val StepRowHeadStyle = CssStyle {
    base {
        Modifier
            .color(Tokens.fg.value())
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "${Tokens.spaceMd.value()}")
                property("width", "100%")
                property("min-height", "${Tokens.barCard.value()}")
                property("padding", "${Tokens.spaceLg.value()}")
                property("background", "none")
                property("border", "none")
                property("text-align", "left")
                property("cursor", "pointer")
            }
    }
    focusVisible { Modifier.focusOutline() }
    cssRule(" > *") {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "flex-start")
            property("gap", "${Tokens.spaceMd.value()}")
            property("width", "100%")
            property("min-width", "0")
        }
    }
}

/**
 * What the open row shows under its header.
 *
 * It arrives with an animation on mount — it is mounted only while its row is open — and only
 * `opacity` and `transform` move, which the compositor does without the main thread.
 */
val StepRowBodyStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "flex-start")
        property("gap", "${Tokens.spaceMd.value()}")
        property("flex", "1 1 auto")
        property("min-height", "0")
        property("padding", "0 ${Tokens.spaceLg.value()} ${Tokens.spaceLg.value()}")
        property("animation", "ha-step-body ${Motion.Slow} ${Motion.Curve} backwards")
    }
}

/** The mark that says a row opens, turned a quarter when it is. */
val StepChevronStyle = CssStyle {
    base {
        Modifier.color(Tokens.lineStrong.value()).styleModifier {
            property("display", "flex")
            property("flex", "0 0 auto")
            property("margin-left", "auto")
            property("transition", "transform ${Motion.Slow} ${Motion.Curve}, color ${Motion.Base} ${Motion.Ease}")
        }
    }
}

/** The mono kicker over a step's title. */
val StepKickerStyle = CssStyle.base {
    Modifier.color(Tokens.accent.value()).styleModifier {
        property("display", "block")
        property("font-family", Tokens.fontMono.value())
        property("font-size", "${Tokens.textXs.value()}")
        property("letter-spacing", Tracking.Eyebrow)
        property("text-transform", "uppercase")
    }
}

/** A step's promise, as the accordion's header says it. */
val StepTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier {
        property("margin", "${Tokens.spaceSm.value()} 0 0")
        property("font-family", Tokens.fontSans.value())
        property("font-size", "${Tokens.textLg.value()}")
        property("font-weight", "${Weight.Black}")
        property("line-height", Leading.Tight)
        property("letter-spacing", Tracking.Display)
        property("padding-right", "3.5rem")
    }
}





/**
 * Where the icon of a step sits: the bottom right corner, in the accent.
 *
 * One mark at one size, grown with a transform when the step opens. Scaling a drawing is not
 * scaling type: there is nothing here to become illegible on the way.
 */
val StepIconStyle = CssStyle.base {
    Modifier.color(Tokens.accent.value()).styleModifier {
        // `flex` and not the default: the glyph inside is an inline element, and width does not
        // apply to one. Without this the icon measures 0 by 0 and nothing shows.
        property("display", "flex")
        property("position", "absolute")
        property("right", "${Tokens.spaceXl.value()}")
        property("bottom", "${Tokens.spaceXl.value()}")
        property("transform-origin", "bottom right")
        property("opacity", "0.45")
        property("pointer-events", "none")
    }
}

/**
 * The light behind a step: a soft wash of the accent, wider once the step is open.
 *
 * Radial and transparent at the edge, so it has no border of its own to line up with anything.
 */
val StepGlowStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "absolute")
        property("right", "-40px")
        property("bottom", "-40px")
        property("width", "10rem")
        property("height", "10rem")
        property("border-radius", "${Tokens.radiusFull.value()}")
        property(
            "background",
            "radial-gradient(circle, ${Tokens.glassRaisedFill.value()} 0%, transparent 70%)",
        )
        property("pointer-events", "none")
    }
}

/** The row of small tags under a step. */
val TagRowStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "${Tokens.spaceSm.value()}")
        property("margin-top", "${Tokens.spaceLg.value()}")
        property("padding", "0")
        property("list-style", "none")
    }
}

/** One tag. */
val TagStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("padding", "${Tokens.spaceXxs.value()} ${Tokens.spaceMd.value()}")
        property("border-radius", "${Tokens.radiusFull.value()}")
        property("background", Tokens.raised.value())
        property("font-family", Tokens.fontMono.value())
        property("font-size", "${Tokens.textXs.value()}")
    }
}

// ---------------------------------------------------------------------------
// What is inside
// ---------------------------------------------------------------------------

/**
 * Four in a row, collapsing to two and then to one.
 *
 * Both card bands use it, so they rhyme without either one owning the grid.
 */
val StatGridStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "grid")
            property("grid-template-columns", "1fr")
            property("gap", "${Tokens.spaceLg.value()}")
        }
    }
    Breakpoint.SM { Modifier.styleModifier { property("grid-template-columns", "repeat(2, 1fr)") } }
    Breakpoint.MD { Modifier.styleModifier { property("grid-template-columns", "repeat(4, 1fr)") } }
}

/** A figure card, with the accent rule along its bottom edge. */
val StatCardStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("position", "relative")
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "${Tokens.spaceSm.value()}")
            property("padding", "${Tokens.spaceXl.value()}")
            property("border-radius", "${Tokens.radiusLg.value()}")
            property("background", Tokens.surface.value())
            property("border", Tokens.glassEdge.value())
            property("border-bottom", "2px solid ${Tokens.accent.value()}")
        }
    }

    // A halo that lights up a pixel outside the card, not a lift. The reference moves almost
    // nothing on hover -it changes color- and it does this one slowly, which is what keeps a
    // row of four from flickering as the pointer crosses them.
    cssRule("::after") {
        Modifier.styleModifier {
            property("content", "\"\"")
            property("position", "absolute")
            property("inset", "-1px")
            property("border-radius", "${Tokens.radiusLg.value()}")
            property("border", "1px solid ${Tokens.accent.value()}")
            property("opacity", "0")
            property("pointer-events", "none")
            property("transition", "opacity ${Motion.Slower} ${Motion.Ease}")
        }
    }
    cssRule(":hover::after") { Modifier.styleModifier { property("opacity", "1") } }
    // It also floats while the pointer rests on it; that lives in `LandingStats`, because
    // leaving has to start from wherever the float had got to.
}

/** The figure itself. */
val StatValueStyle = CssStyle.base {
    Modifier.color(Tokens.accent.value()).styleModifier {
        property("font-family", Tokens.fontSans.value())
        property("font-size", "${Tokens.textDisplay.value()}")
        property("font-weight", "${Weight.Black}")
        property("line-height", Leading.Display)
        property("letter-spacing", Tracking.Display)
    }
}

/** What the figure counts. */
val StatLabelStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier {
        property("font-weight", "${Weight.Semibold}")
        property("line-height", Leading.Snug)
    }
}

/** The sentence under it. */
val StatBodyStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("font-family", Tokens.fontMono.value())
        property("font-size", "${Tokens.textXs.value()}")
        property("line-height", Leading.Normal)
    }
}

// ---------------------------------------------------------------------------
// Who it is for
// ---------------------------------------------------------------------------

/** A card with an icon tile, a title, a sentence and a link out. */
/**
 * A card that turns over to the accent under the pointer, text and all.
 *
 * This is the reference's boldest hover and it does not move a pixel: the fill goes to the
 * accent, every piece of text inside goes to what reads on it, and the icon tile swaps its two
 * colors. Measured there at 300 ms, which lands on our own `slow` step.
 *
 * The parts are addressed by the `data-part` the markup puts on them rather than by the class
 * names Silk generates from property names, which would break silently on a rename.
 */
val AudienceCardStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-start")
            property("gap", "${Tokens.spaceMd.value()}")
            property("padding", "${Tokens.spaceXl.value()}")
            property("border-radius", "${Tokens.radiusLg.value()}")
            property("background", Tokens.surface.value())
            property("border", Tokens.glassEdge.value())
            property("height", "100%")
            property(
                "transition",
                "background ${Motion.Slow} ${Motion.Ease}, border-color ${Motion.Slow} ${Motion.Ease}",
            )
        }
    }
    hover {
        Modifier.styleModifier {
            property("background", Tokens.accent.value())
            property("border-color", Tokens.accent.value())
        }
    }
    cssRule(":hover [data-part=\"tile\"]") {
        Modifier.styleModifier {
            property("background", Tokens.onAccent.value())
            property("color", Tokens.accent.value())
        }
    }
    cssRule(":hover [data-part=\"title\"]") {
        Modifier.styleModifier { property("color", Tokens.onAccent.value()) }
    }
    // Full strength, unlike the reference, which fades this line to 60%. It can: its accent is
    // a bright lime and near-black survives the fade on it. Ours is a cobalt, and the same 60%
    // measured 4.32:1 against the fill - under the floor for a line this small. The hierarchy
    // was never the opacity anyway; it is that the title is twenty pixels of black weight and
    // this is twelve of mono.
    cssRule(":hover [data-part=\"body\"]") {
        Modifier.styleModifier { property("color", Tokens.onAccent.value()) }
    }
    cssRule(":hover [data-part=\"link\"]") {
        Modifier.styleModifier { property("color", Tokens.onAccent.value()) }
    }
}

/** The rounded tile the glyph sits on. */
val IconTileStyle = CssStyle.base {
    Modifier.color(Tokens.onAccent.value()).backgroundColor(Tokens.accent.value()).styleModifier {
        property("transition", "background ${Motion.Slow} ${Motion.Ease}, color ${Motion.Slow} ${Motion.Ease}")
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("width", "$IconTile")
        property("height", "$IconTile")
        property("border-radius", "${Tokens.radiusMd.value()}")
        property("flex-shrink", "0")
    }
}

/** A card's title. */
val CardTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier {
        property("transition", "color ${Motion.Slow} ${Motion.Ease}")
        property("margin", "0")
        property("font-family", Tokens.fontSans.value())
        property("font-size", "${Tokens.textMd.value()}")
        property("font-weight", "${Weight.Black}")
        property("line-height", Leading.Snug)
        property("letter-spacing", Tracking.Tight)
    }
}

/** A card's sentence. */
val CardBodyStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("transition", "color ${Motion.Slow} ${Motion.Ease}, opacity ${Motion.Slow} ${Motion.Ease}")
        property("margin", "0")
        property("font-family", Tokens.fontMono.value())
        property("font-size", "${Tokens.textXs.value()}")
        property("line-height", Leading.Normal)
    }
}

/** The accent link at the foot of a card, with its arrow. */
val CardLinkStyle = CssStyle {
    base {
        Modifier.color(Tokens.accent.value()).styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceSm.value()}")
            property("margin-top", "auto")
            property("padding-top", "${Tokens.spaceSm.value()}")
            // The call of a card is the one thing on it anybody presses, and it was 25 px tall.
            property("min-height", "$TapTarget")
            property("font-size", "${Tokens.textSm.value()}")
            property("font-weight", "${Weight.Semibold}")
            property("text-decoration", "none")
            property("transition", "color ${Motion.Slow} ${Motion.Ease}")
        }
    }
    hover { Modifier.styleModifier { property("text-decoration", "underline") } }
    focusVisible { Modifier.focusOutline() }

    // The arrow leans forward when the link is under the pointer. It is addressed by the
    // attribute the markup puts on it and not by its generated class name, which is derived
    // from a property name and would break silently on a rename.
    cssRule(":hover [data-arrow]") {
        Modifier.styleModifier { property("transform", "translateX(3px)") }
    }
}

/** The arrow at the end of a card link: the card's only piece of motion. */
val CardArrowStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "inline-flex")
        property("transition", "transform ${Motion.Base} ${Motion.Curve}")
    }
}

// ---------------------------------------------------------------------------
// The scrolling strip
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// The closing call and the footer
// ---------------------------------------------------------------------------

/** The last band: one headline, one field, one button. */
val CtaStyle = CssStyle.base {
    Modifier.styleModifier {
        property("text-align", "center")
        property("padding-block", "$SectionPadCompact ${Tokens.spaceXxxl.value()}")
    }
}

/** The field and its button, side by side down to the narrowest screen. */
val CtaFormStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("justify-content", "center")
        property("gap", "${Tokens.spaceMd.value()}")
        property("margin", "${Tokens.spaceXl.value()} auto 0")
        property("max-width", "$FormWidth")
    }
}

/**
 * The grid of examples: three across on a wide screen, two on a tablet, one on a phone.
 *
 * `auto-fit` with a minimum rather than fixed counts, so the number of columns follows the
 * width and there is no breakpoint to keep in step with the number of examples.
 */
val SampleGridStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "grid")
        property("grid-template-columns", "repeat(auto-fit, minmax(17rem, 1fr))")
        property("gap", "${Tokens.spaceLg.value()}")
        property("margin-top", "${Tokens.spaceXl.value()}")
    }
}

/**
 * One example: the page fills the card and the caption sits on it.
 *
 * On hover only the border changes: the bitácora records that these cards used to rise 4px on
 * their own initiative and that it was taken out.
 */
val SampleCardStyle = CssStyle {
    base {
        Modifier.color(Tokens.fg.value()).styleModifier {
            property("position", "relative")
            property("display", "block")
            property("height", "${SHOT_CARD_HEIGHT}px")
            property("border-radius", "${Tokens.radiusLg.value()}")
            property("overflow", "hidden")
            property("background", Tokens.raised.value())
            property("border", Tokens.glassEdge.value())
            property("text-decoration", "none")
            property("transition", "border-color ${Motion.Base} ${Motion.Ease}")
        }
    }
    hover { Modifier.styleModifier { property("border-color", Tokens.accent.value()) } }
}

/** The screenshot, filling the card from the top down. */
val SampleShotStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "absolute")
        property("inset", "0")
        property("width", "100%")
        property("height", "100%")
        property("object-fit", "cover")
        property("object-position", "top center")
        // A load failure is a block, not a broken icon.
        property("background", Tokens.glassWellFill.value())
    }
}

/**
 * The caption, over the page it describes.
 *
 * The blur is what makes this work on ANY screenshot: a gradient alone reads fine over a dark
 * cover and fights the page's own words over a light one — Ferro Norte is a white dashboard.
 * Blurring what is underneath dissolves that text instead of stacking ours on top of it, and the
 * mask keeps the blur from cutting a hard line across the middle of the page.
 */
val SampleFootStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "absolute")
        property("left", "0")
        property("right", "0")
        property("bottom", "0")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Tokens.spaceSm.value()}")
        property("padding", "${Tokens.spaceLg.value()}")
        property("background", Tokens.shotVeil.value())
        property("backdrop-filter", "blur(7px)")
        property("-webkit-backdrop-filter", "blur(7px)")
        property("mask-image", "linear-gradient(180deg, transparent 0%, #000 30%)")
        property("-webkit-mask-image", "linear-gradient(180deg, transparent 0%, #000 30%)")
    }
}

/** What kind of page it is, quieter than the name. */
val SampleKindStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("font-family", Tokens.fontMono.value())
        property("text-transform", "uppercase")
        property("letter-spacing", "0.08em")
    }
}

/** The name of the business, which is what somebody recognises the page by. */
val SampleNameStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textMd.value()).styleModifier {
        property("font-weight", "${Weight.Semibold}")
        property("line-height", Leading.Tight)
    }
}

/**
 * What was asked for and what the agent decided, which is what makes a card worth opening.
 *
 * Two lines and no more: the caption is the reason to press the card, the page under it is the
 * argument, and a third line of ours takes another slice of the page away.
 */
val SampleLineStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("margin", "0")
        property("line-height", Leading.Snug)
        property("display", "-webkit-box")
        property("-webkit-line-clamp", "2")
        property("-webkit-box-orient", "vertical")
        property("overflow", "hidden")
    }
}

/**
 * The action, bottom right, as a pill rather than a lone arrow.
 *
 * It is not a button: the whole card is the link. This says out loud what pressing it does,
 * which a 15px arrow in the corner never did.
 */
val SampleActionStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("justify-content", "flex-end")
        property("margin-top", "${Tokens.spaceXs.value()}")
    }
}

/** The pill itself. */
val SamplePillStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceSm.value()}")
        property("height", "${Tokens.controlMd.value()}")
        property("padding", "0 ${Tokens.spaceXl.value()}")
        property("border-radius", "${Tokens.radiusFull.value()}")
        property("background", Tokens.glassPanelFill.value())
        property("border", Tokens.glassEdge.value())
        property("font-weight", "${Weight.Semibold}")
        property("white-space", "nowrap")
    }
}

/** How tall an example card is. Tall enough for the page to be legible above its caption. */
private const val SHOT_CARD_HEIGHT = 380

/**
 * The tip strip: one line of text and a quiet link, centred.
 *
 * It is deliberately the flattest band on the page - no card, no figure, no accent fill. It sits
 * where somebody has just read what they get for nothing, and it must never look like the price
 * of it, nor compete with the one button this page is for.
 */
val TipBandStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("gap", "${Tokens.spaceMd.value()}")
        property("padding", "${Tokens.spaceXl.value()} 0")
        property("border-top", Tokens.glassEdge.value())
        property("border-bottom", Tokens.glassEdge.value())
        property("text-align", "center")
    }
}

/** The invitation, in sentence case: it is a sentence, not a headline. */
val TipTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textXl.value()).styleModifier {
        property("margin", "0")
        property("font-weight", "${Weight.Semibold}")
        property("line-height", Leading.Tight)
    }
}

/** What it costs and what it does not: the two lines under the invitation. */
val TipBodyStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("margin", "0")
        property("max-width", "46ch")
        property("line-height", Leading.Normal)
    }
}

/**
 * The link out to Ko-fi, dressed as the ghost pill and never as the accent one.
 *
 * It is an anchor and not a [PillGhostVariant] button because it leaves for another site, and
 * the difference matters to anybody who reads the status bar before clicking.
 */
val TipLinkStyle = CssStyle {
    base {
        Modifier.color(Tokens.fg.value()).styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceSm.value()}")
            property("min-height", "$TapTarget")
            property("padding", "0 ${Tokens.spaceXl.value()}")
            property("border", "1px solid ${Tokens.lineStrong.value()}")
            property("border-radius", "${Tokens.radiusFull.value()}")
            property("font-weight", "${Weight.Semibold}")
            property("text-decoration", "none")
            property("transition", "border-color ${Motion.Base} ${Motion.Ease}")
        }
    }
    hover { Modifier.styleModifier { property("border-color", Tokens.accent.value()) } }
}

/**
 * The same invitation inside the chat, one line and no pill.
 *
 * The workspace is not a page to be persuaded on: after the download it is a sentence with an
 * underline, and it never returns.
 */
val TipInlineStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).fontSize(Tokens.textSm.value()).styleModifier {
            // A block as wide as its words: inline, it neither kept the conversation's step below
            // it nor pushed the next message away, and sat against it.
            property("display", "block")
            property("width", "fit-content")
            property("margin-bottom", "${Tokens.spaceXl.value()}")
            property("text-decoration", "underline")
            property("text-underline-offset", "3px")
            property("transition", "color ${Motion.Base} ${Motion.Ease}")
        }
    }
    hover { Modifier.color(Tokens.accent.value()) }
}

/** The amount, quieter than the link above it: a suggestion, not a price. */
val TipAmountStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textXs.value()).styleModifier {
        property("margin", "0")
        property("font-family", Tokens.fontMono.value())
    }
}

/**
 * The questions, two to a row on a wide screen.
 *
 * They are laid out and not folded away: an answer behind a disclosure is an answer nobody
 * reads, and these six exist to be found by whoever is already hesitating.
 */
/**
 * The questions, one under another at reading width.
 *
 * It was a grid of three columns, and three columns turn a sequence into a Z. The six are
 * ordered — curiosity, then the objections, then the one that names a competitor and ends by
 * turning work away — and in three columns that last one landed in the bottom right corner,
 * which is the least-read place on a page. One column gives the order back.
 *
 * The columns also left dead air: the answers run from 27 to 58 words, and a row is as tall as
 * its longest, so the shortest answer sat above 72 px of nothing.
 */
val FaqListStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("max-width", "68ch")
        property("margin", "${Tokens.spaceXl.value()} auto 0")
    }
}

/**
 * One question. The whole row is the control, and the answer under it folds.
 *
 * A FAQ is something you consult, not something you read: six answers open at once is 234 words
 * to wade through to find the one that matters. Folded, the six questions fit on one screen —
 * measured on a phone, the band goes from 1 401 px, nearly two screenfuls right before the call
 * to action, to a list you can take in at a glance.
 */
val FaqItemStyle = CssStyle {
    base {
        Modifier.color(Tokens.fg.value()).styleModifier {
            property("width", "100%")
            property("height", "auto")
            property("padding", "0")
            property("background", "transparent")
            property("border", "none")
            property("border-top", Tokens.glassEdge.value())
            property("border-radius", "0")
            property("font-family", Tokens.fontSans.value())
            property("font-size", "1rem")
            property("font-weight", "400")
            property("text-align", "left")
            property("white-space", "normal")
            property("cursor", "pointer")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "stretch")
        }
    }
    // Silk wraps a button's children in a row of its own. Left as it comes, that row squeezed
    // the question against the chevron and a header wrapped to five lines on a phone: 175 px
    // for one question. The column belongs here.
    cssRule(" > *") {
        Modifier.styleModifier {
            property("width", "100%")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "stretch")
            property("gap", "0")
        }
    }
    cssRule("[data-open=\"true\"] [data-answer]") {
        Modifier.styleModifier { property("grid-template-rows", "1fr") }
    }
    cssRule("[data-open=\"true\"] [data-chevron]") {
        Modifier.color(Tokens.accent.value()).styleModifier {
            property("transform", "rotate(180deg)")
        }
    }
    hover { Modifier.color(Tokens.accent.value()) }
    focusVisible { Modifier.focusOutline() }
}

/** The row you press: the question, and the mark that says it opens. */
val FaqRowStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceLg.value()}")
        property("min-height", "$TapTarget")
        property("padding", "${Tokens.spaceMd.value()} 0")
    }
}

/**
 * The question, in the reader's own words.
 *
 * `textMd` and not `textLg`: at 25 px these are headlines and only three fit on a phone screen.
 * The point of the fold is that all six can be scanned at once, and that needs them smaller.
 */
val FaqQuestionStyle = CssStyle {
    base {
        Modifier.color(Tokens.fg.value()).fontSize(Tokens.textBase.value()).styleModifier {
            property("flex", "1")
            property("margin", "0")
            property("font-weight", "${Weight.Semibold}")
            property("line-height", Leading.Snug)
        }
    }
    // Measured at 390 px with the desktop size: the questions wrapped to three and four lines
    // and a header row reached 213 px, which is the wall this fold exists to remove. One step
    // down and they take one line or two.
    Breakpoint.SM { Modifier.fontSize(Tokens.textMd.value()) }
}

/** The one thing that says a row can be pressed. Without it a list is just a list. */
val FaqChevronStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("display", "flex")
        property("flex", "none")
        property(
            "transition",
            "transform ${Motion.Base} ${Motion.Curve}, color ${Motion.Base} ${Motion.Ease}",
        )
    }
}

/** The fold. Rows and not height, for the reason written on the steps: `auto` cannot be walked to. */
val FaqAnswerStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "grid")
        property("grid-template-rows", "0fr")
        property("transition", "grid-template-rows ${Motion.Slow} ${Motion.Curve}")
    }
}

/** Its contents. `min-height: 0` is what lets a grid row of `0fr` actually close. */
val FaqAnswerInnerStyle = CssStyle.base {
    Modifier.styleModifier {
        property("overflow", "hidden")
        property("min-height", "0")
    }
}

/** The answer, which is allowed to say no. */
val FaqAnswerTextStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("margin", "0")
        property("padding-bottom", "${Tokens.spaceLg.value()}")
        property("line-height", Leading.Normal)
    }
}

/** The button that repeats the page's one action, centred above the field for coming back. */
val CtaActionsStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("justify-content", "center")
        property("margin", "${Tokens.spaceXl.value()} 0 0")
    }
}

/** The line that hands the field below it to whoever has been here before. */
val CtaReturningStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("margin", "${Tokens.spaceXl.value()} 0 0")
            property("font-size", "${Tokens.textSm.value()}")
        }
    }
    cssRule("[data-align=\"center\"]") { Modifier.styleModifier { property("text-align", "center") } }
}

/** The email field. Sunk rather than raised, like the composer in the application. */
val CtaInputVariant = InputStyle.addVariant {
    base {
        Modifier.color(Tokens.fg.value()).styleModifier {
            property("flex", "1 1 240px")
            property("min-height", "${Tokens.controlLg.value()}")
            property("padding", "0 ${Tokens.spaceLg.value()}")
            property("background", Tokens.glassWellFill.value())
            property("border", "1px solid ${Tokens.lineStrong.value()}")
            property("border-radius", "${Tokens.radiusFull.value()}")
        }
    }
    focusVisible { Modifier.focusOutline() }
}

/** The line under the form that says what the email is and is not for. */
val CtaNoteStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("margin", "${Tokens.spaceMd.value()} 0 0")
            property("font-family", Tokens.fontMono.value())
            property("font-size", "${Tokens.textXs.value()}")
        }
    }
    // The same note under a band that is not itself centred.
    cssRule("[data-align=\"center\"]") { Modifier.styleModifier { property("text-align", "center") } }
}

/** The foot of the page, inside the same ink band as the call above it. */
val FooterStyle = CssStyle.base {
    Modifier.styleModifier {
        property("padding-bottom", "${Tokens.spaceXxl.value()}")
    }
}

/** The sentence that says what the product is, for whoever landed here from a link. */
val FooterAboutStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("margin", "0 auto")
        property("max-width", "62ch")
        property("padding-bottom", "${Tokens.spaceXxl.value()}")
        property("font-family", Tokens.fontMono.value())
        property("font-size", "${Tokens.textXs.value()}")
        property("line-height", Leading.Normal)
        property("text-align", "center")
        property("border-bottom", "1px solid ${Tokens.line.value()}")
    }
}

/**
 * The one row of the foot: where the page's sections are, and the two switches.
 *
 * It was a two-column grid with a heading over each. Measured at 1180 px, the grid gave each
 * column 546 px to hold 96 px of links, and on a phone the whole foot came to 673 px — nine
 * tenths of a screen for five links and two buttons. Six things are a row, not a grid; a grid
 * that size only stops looking empty if it is filled with a privacy policy and a press page,
 * and inventing doors onto nothing is the one thing this page cannot do.
 */
val FooterNavStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-start")
            property("gap", "${Tokens.spaceLg.value()}")
            property("padding", "${Tokens.spaceXl.value()} 0")
        }
    }
    Breakpoint.SM {
        Modifier.styleModifier {
            property("flex-direction", "row")
            property("align-items", "center")
            property("justify-content", "space-between")
        }
    }
}

/** The section links, in a row that wraps. No heading: a row of anchors says what it is. */
val FooterLinksStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", "0 ${Tokens.spaceXl.value()}")
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
    }
}

/**
 * One link in the row.
 *
 * The same face as the anchors in the bar — sans, not mono, and the same size — because they go
 * to the same six places. Written differently at the two ends of the page, the same destination
 * reads as two different things.
 */
val FooterLinkStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            // The box is the pointer target, never the type: at 14 px these were 14 px strips.
            property("display", "inline-flex")
            property("align-items", "center")
            property("min-height", "$TapTarget")
            property("font-size", "${Tokens.textSm.value()}")
            property("font-weight", "${Weight.Semibold}")
            property("text-decoration", "none")
            // A menu item never wraps. Without this the anchors were the one thing in the row
            // that gave way when the bar ran short, and "Qué te llevas" arrived on three lines
            // at 46 px wide - a label nobody reads and a bar nobody trusts.
            property("white-space", "nowrap")
            property("transition", "color ${Motion.Fast} ${Motion.Ease}")
        }
    }
    // To the accent, as in the reference: in the footer it is the only color left to use.
    hover { Modifier.color(Tokens.accent.value()) }
    focusVisible { Modifier.focusOutline() }
}

/**
 * The two switches, beside the links rather than under a heading of their own.
 *
 * "Ajustes" in bold over two icon buttons said section where there was a pair of controls, and
 * it put them inside a list, which is what a screen reader then announced them as.
 */
val FooterSwitchesStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Tokens.spaceXs.value()}")
    }
}

/** The last row: the mark, the copyright and the sign-off. */
val FooterBottomStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("gap", "${Tokens.spaceMd.value()}")
            property("padding-top", "${Tokens.spaceLg.value()}")
            property("border-top", "1px solid ${Tokens.line.value()}")
            property("font-family", Tokens.fontMono.value())
            property("font-size", "${Tokens.textXs.value()}")
        }
    }
    Breakpoint.SM {
        Modifier.styleModifier {
            property("flex-direction", "row")
            property("justify-content", "space-between")
        }
    }
}

/**
 * The copyright and the sign-off: two fragments of the same small print.
 *
 * On a phone they are two centred lines, because on one they came to 341 px inside 342 and any
 * longer sign-off would have wrapped at the last word. From a tablet they share a line, and there
 * the dot between them is drawn rather than written — without it the line reads "© 2026 HAL Hecho
 * con Kotlin". The dot arrives with the row and not before it: at the head of a wrapped second
 * line it would be a bullet nobody asked for.
 */
val FooterFinePrintStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
        }
    }
    Breakpoint.SM {
        Modifier.styleModifier {
            property("flex-direction", "row")
            property("gap", "${Tokens.spaceSm.value()}")
            property("--fine-dot", "\"·\"")
        }
    }
    // A custom property and not a second media query, because `cssRule` takes a raw
    // `CSSMediaQuery` and a `Breakpoint` is not one. `content: none` draws no box at all, so
    // below a tablet the separator does not exist rather than being hidden.
    cssRule("> span + span::before") {
        Modifier.styleModifier {
            property("content", "var(--fine-dot, none)")
            property("margin-right", "${Tokens.spaceSm.value()}")
        }
    }
}

/** Below a tablet the nav actions shed the two switches: the call to action wins the room. */
val NavSwitchesStyle = CssStyle {
    base { Modifier.styleModifier { property("display", "none") } }
    // They come back at the same width the anchors do, and not one breakpoint earlier.
    //
    // Measured at 480 px with them showing: mark, two switches, the pill and the menu button
    // came to 497 in a 480 viewport, and what fell off the right edge was the menu button —
    // the one control that width has instead of a bar. Below MD the sheet carries them, so
    // showing them here was both an overflow and a duplicate.
    Breakpoint.LG {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceXs.value()}")
        }
    }
}

/** A bare icon control in the bar: the color mode and the language. */
val NavSwitchVariant = ButtonStyle.addVariant {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("width", "$TapTarget")
            property("height", "$TapTarget")
            property("padding", "0")
            property("background", "transparent")
            property("border", "none")
            property("border-radius", "${Tokens.radiusMd.value()}")
        }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.accentWash.value()) } }
    focusVisible { Modifier.focusOutline() }
}

// ---------------------------------------------------------------------------
// The entry dialog
// ---------------------------------------------------------------------------

/**
 * What covers the landing while the entry card is open.
 *
 * It fades and it does not blur: behind it there is a page nine thousand pixels long whose
 * glows and reveals would each be recomputed per frame by a `backdrop-filter` over all of it.
 * The tint alone is enough to say the page is waiting.
 */
val EnterScrimStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "fixed")
        property("inset", "0")
        property("z-index", "${Layer.Popover}")
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("padding", "${Tokens.spaceXl.value()}")
        property("background", Tokens.modalScrim.value())
        property("animation", "ha-brief-in ${Motion.Base} ${Motion.Ease} backwards")
    }
}

/**
 * The card itself, at the entrance screen's width so both doors look like the same door.
 *
 * Opaque rather than glass: it sits over a scrim, and a panel that blurs what is behind it
 * would be blurring a tint that is already flat.
 */
val EnterCardStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.surface.value()).color(Tokens.fg.value()).styleModifier {
        property("position", "relative")
        property("width", "min(420px, 100%)")
        property("max-height", "100%")
        property("overflow", "auto")
        property("box-sizing", "border-box")
        property("padding", "${Tokens.spaceXxl.value()}")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Tokens.spaceLg.value()}")
        property("text-align", "left")
        property("border", "1px solid ${Tokens.line.value()}")
        property("border-radius", "${Tokens.radiusLg.value()}")
        property("box-shadow", "0 32px 80px rgba(0, 0, 0, 0.45)")
        property("animation", "ha-brief-rise ${Motion.Base} ${Motion.Curve} backwards")
    }
}

/** The title of the card, which says what this door is for and not what the product is. */
val EnterTitleStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).fontSize(Tokens.textLg.value()).styleModifier {
        property("margin", "0")
        property("font-weight", "${Weight.Bold}")
        property("letter-spacing", Tracking.Tight)
    }
}

/** One line under the title: what typing an address here gets you. */
val EnterLeadStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).fontSize(Tokens.textSm.value()).styleModifier {
        property("margin", "${Tokens.spaceXs.value()} 0 0")
        property("line-height", Leading.Normal)
    }
}

/** The way out, in the corner everybody looks for it in. */
val EnterCloseStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("position", "absolute")
            property("top", "${Tokens.spaceMd.value()}")
            property("right", "${Tokens.spaceMd.value()}")
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("width", "${Tokens.controlSm.value()}")
            property("height", "${Tokens.controlSm.value()}")
            property("padding", "0")
            property("background", "transparent")
            property("border", "1px solid transparent")
            property("border-radius", "${Tokens.radiusSm.value()}")
            property("cursor", "pointer")
        }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.raised.value()) } }
    focusVisible { Modifier.focusOutline() }
}


// ---------------------------------------------------------------------------
// The install offer
// ---------------------------------------------------------------------------

/**
 * "Install app", floating over the page instead of standing in the bar.
 *
 * It is an offer and not a destination, so it does not belong in a row of destinations: in the
 * bar it was 112 px of the 68 the row had left. Here it costs the layout nothing, and it can
 * afford to wait - it appears once the hero has scrolled past, because an offer to install
 * something makes no sense to somebody who has not yet seen what it is.
 *
 * Under [Layer.Popover] on purpose: the entry dialog opens over the whole page, this included.
 */
val InstallFabStyle = CssStyle {
    base {
        Modifier.color(Tokens.fg.value()).backgroundColor(Tokens.raised.value()).styleModifier {
            property("position", "fixed")
            property("right", "${Tokens.spaceXl.value()}")
            property("bottom", "calc(${Tokens.spaceXl.value()} + env(safe-area-inset-bottom, 0px))")
            property("z-index", "${Layer.Overlay}")
            property("display", "flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceXs.value()}")
            property("padding", "${Tokens.spaceXs.value()} ${Tokens.spaceXs.value()} " +
                "${Tokens.spaceXs.value()} ${Tokens.spaceLg.value()}")
            property("border", "1px solid ${Tokens.line.value()}")
            property("border-radius", "${Tokens.radiusFull.value()}")
            property("box-shadow", Tokens.glassPanelShadow.value())
            property("animation", "ha-brief-in ${Motion.Base} ${Motion.Ease} backwards")
        }
    }
}

/** The offer itself: the icon, the words, and the whole thing is the button. */
val InstallFabActionStyle = CssStyle {
    base {
        Modifier.color(Tokens.fg.value()).fontSize(Tokens.textSm.value()).styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceSm.value()}")
            property("min-height", "${Tokens.controlSm.value()}")
            property("padding", "0")
            property("background", "transparent")
            property("border", "none")
            property("font-weight", "${Weight.Semibold}")
            property("white-space", "nowrap")
            property("cursor", "pointer")
        }
    }
    focusVisible { Modifier.focusOutline() }
}

/**
 * The way to make it go away.
 *
 * A thing that floats over the page and cannot be dismissed is not an offer, it is an
 * obstruction - and this one sits exactly where a phone's thumb rests.
 */
val InstallFabDismissStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("width", "${Tokens.controlSm.value()}")
            property("height", "${Tokens.controlSm.value()}")
            property("flex", "none")
            property("padding", "0")
            property("background", "transparent")
            property("border", "none")
            property("border-radius", "${Tokens.radiusFull.value()}")
            property("cursor", "pointer")
        }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.surface.value()) } }
    focusVisible { Modifier.focusOutline() }
}
